import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {setWindowTitle} from 'BenchlingUtil';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {LabelPrintingMethod} from 'label-printing/LabelPrintingConstants';
import navigateToURL from 'lib/navigateToURL';
import * as RegistryUtil from 'registry/RegistryUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {Header1} from 'ui-library/Headers';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {LabelPrintingSettingsContainerQuery} from './__generated/LabelPrintingSettingsContainerQuery';
import LabelTemplatesListing from './LabelTemplatesListing';
import PrintersListing from './PrintersListing';

type LabelPrintingSettingsTab = ModelTypeConstants.LABEL_TEMPLATE | ModelTypeConstants.PRINTER;

export interface LabelPrintingSettingsContainerProps {
  activeTab: LabelPrintingSettingsTab;
  ownerUrlId: string;
  registryId: string;
  isWritable: boolean;
}

export const LABEL_PRINTING_SETTINGS_CONTAINER_QUERY = gql`
  query LabelPrintingSettingsContainerQuery @skipAudit @engComponent(name: INVENTORY) {
    labelPrintingConfig {
      id
      tenantConfiguredPrintingMethod
    }
  }
`;

export function LabelPrintingSettingsContainer(props: LabelPrintingSettingsContainerProps): JSX.Element {
  const queryResult = useBenchlingQuery<LabelPrintingSettingsContainerQuery>(
    LABEL_PRINTING_SETTINGS_CONTAINER_QUERY
  );

  const handleSetActiveTab = (tab: LabelPrintingSettingsTab): void => {
    if (tab === ModelTypeConstants.LABEL_TEMPLATE) {
      navigateToURL(RegistryUtil.getRegistryLabelTemplatesUrl(props.ownerUrlId, props.registryId));
    } else {
      assert(tab === ModelTypeConstants.PRINTER, 'Invalid tab');
      navigateToURL(RegistryUtil.getRegistryPrintersUrl(props.ownerUrlId, props.registryId));
    }
  };

  const handleCreate = (): void => {
    if (props.activeTab === ModelTypeConstants.LABEL_TEMPLATE) {
      navigateToURL(RegistryUtil.getCreateLabelTemplateUrl(props.ownerUrlId, props.registryId));
    } else {
      assert(props.activeTab === ModelTypeConstants.PRINTER, 'Invalid tab');
      navigateToURL(RegistryUtil.getCreatePrinterUrl(props.ownerUrlId, props.registryId));
    }
  };

  setWindowTitle('Label Printing - Registry Settings');
  return (
    <div className="settingsPage">
      <div className="settingsPage-contentWrapper">
        <div className={classNames('u-lr-split', css(styles.settingsPageHader))}>
          <Header1>Label printing</Header1>
          {props.isWritable && (
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              onClick={handleCreate}
              size={ButtonSize.SMALL}
              text={
                props.activeTab === ModelTypeConstants.LABEL_TEMPLATE
                  ? 'Create Label Template'
                  : 'Create Printer'
              }
            />
          )}
        </div>
        {queryResult.type === QueryResultType.SUCCESS &&
          queryResult.data.labelPrintingConfig.tenantConfiguredPrintingMethod ===
            LabelPrintingMethod.BARTENDER && (
            <div className={css(styles.marginTop35px)}>
              <InlineBanner
                bannerType={InlineBannerType.WARNING}
                text={
                  <>
                    Your tenant is using BarTender as its printing method, which overrides any label printing
                    settings in this registry. To change your tenant's printing method, go to{' '}
                    <a href="/label-printing-settings" onClick={BenchlingUtil.navigate}>
                      Label Printing
                    </a>
                    .
                  </>
                }
              />
            </div>
          )}
        <TabContainer
          activeTab={props.activeTab}
          className={classNames('u-flex-grow', css(styles.marginTop35px))}
          onRequestTabChange={handleSetActiveTab}
        >
          <Tab tabKey={ModelTypeConstants.LABEL_TEMPLATE} title="Label Templates">
            <LabelTemplatesListing
              isWritable={props.isWritable}
              ownerUrlId={props.ownerUrlId}
              registryId={props.registryId}
            />
          </Tab>
          <Tab tabKey={ModelTypeConstants.PRINTER} title="Printers">
            <PrintersListing
              isWritable={props.isWritable}
              ownerUrlId={props.ownerUrlId}
              registryId={props.registryId}
            />
          </Tab>
        </TabContainer>
      </div>
    </div>
  );
}

function QueryingLabelPrintingSettingsContainer(
  props: Omit<LabelPrintingSettingsContainerProps, 'isWritable'>
): JSX.Element {
  const isWritable = RegistryUtil.useIsRegistryWritableQuery(props.registryId);
  return <LabelPrintingSettingsContainer {...props} isWritable={isWritable} />;
}

export default QueryingLabelPrintingSettingsContainer;

const styles = StyleSheet.create({
  settingsPageHader: {
    alignItems: 'center',
    display: 'flex',
  },
  marginTop35px: {
    marginTop: '35px',
  },
});
